/**
 * Collection Vehicle Data Manager
 * Handles data retrieval and management for vehicle display
 */
class CollectionVehicleData {
  static STORAGE_KEYS = {
    METAOBJECT_ID: 'cgfinder-metaobjectId',
    YEAR: 'cgfinder-year-select',
    MODEL: 'cgfinder-model-select',
    DETAILS: 'cgfinder-details-select',
    CATEGORY: 'cgfinder-category-select',
    SEARCH_FIELD: 'cgfinder-search-field',
    STORAGE_KEY: 'cgfinder-selections'
  };

  constructor() {
    this.cache = new Map();
  }

  /**
   * Get comprehensive vehicle data from all available sources
   */
  getVehicleData() {
    const cacheKey = 'vehicleData';
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    let vehicleData = null;

    // Method 1: Check current form values first (for real-time updates)
    const currentFormData = this.getCurrentFormValues();
    if (currentFormData && this.hasValidVehicleData(currentFormData)) {
      vehicleData = currentFormData;
      console.log('📊 Using current form values for display:', vehicleData);
    }
    // Method 2: Use CGFinder's comprehensive method if available
    else if (this.isCGFinderAvailable()) {
      vehicleData = this.getCGFinderData();
    } 
    // Method 3: Fallback to manual localStorage parsing
    else {
      vehicleData = this.getLocalStorageData();
    }

    // Cache the result for performance (shorter cache for form values)
    const cacheTime = currentFormData ? 1000 : 5000; // 1s for form data, 5s for stored data
    this.cache.set(cacheKey, vehicleData);
    setTimeout(() => this.cache.delete(cacheKey), cacheTime);
    
    return vehicleData;
  }

  /**
   * Check if CGFinder is available and has required methods
   */
  isCGFinderAvailable() {
    return typeof window.CGFinder !== 'undefined' && 
           typeof window.CGFinder.getCGFinderDataWithUrlFallback === 'function';
  }

  /**
   * Get data using CGFinder's comprehensive method
   */
  getCGFinderData() {
    try {
      const data = window.CGFinder.getCGFinderDataWithUrlFallback();
      console.log('📊 Retrieved vehicle data via CGFinder:', data);
      return this.normalizeVehicleData(data);
    } catch (error) {
      console.warn('Error getting CGFinder data, falling back to localStorage:', error);
      return this.getLocalStorageData();
    }
  }

  /**
   * Get current form values (for real-time display updates)
   */
  getCurrentFormValues() {
    try {
      // Check if any collection vehicle display is showing the default form
      // If so, don't return form values to avoid conflicting displays
      const resultsDisplays = document.querySelectorAll('.collection-vehicle-results');
      const defaultFormsVisible = document.querySelectorAll('.collection-easysearch-custom .title-easysearch');
      
      let hasVisibleDefaultForm = false;
      defaultFormsVisible.forEach(form => {
        if (form.style.display !== 'none') {
          hasVisibleDefaultForm = true;
        }
      });
      
      // If the default form is currently visible, don't use form values for display
      if (hasVisibleDefaultForm) {
        return null;
      }
      
      // Try to get current form values from active CGFinder forms
      const yearSelect = document.querySelector('select[name="cgfinder-year-select"]');
      const modelSelect = document.querySelector('select[name="cgfinder-model-select"]');
      const detailsSelect = document.querySelector('select[name="cgfinder-details-select"]');
      
      if (yearSelect || modelSelect || detailsSelect) {
        const currentData = {
          year: yearSelect?.value || null,
          model: modelSelect?.value || null,
          details: detailsSelect?.value || null,
          metaobjectId: null,
          searchField: null
        };

        // Only return if we have meaningful data
        if (currentData.year && currentData.model) {
          // Try to get metaobjectId from localStorage if available
          const storedData = this.getLocalStorageData();
          currentData.metaobjectId = storedData.metaobjectId;
          currentData.searchField = storedData.searchField;
          
          return this.normalizeVehicleData(currentData);
        }
      }
    } catch (error) {
      console.warn('Error getting current form values:', error);
    }
    
    return null;
  }

  /**
   * Get data directly from localStorage (fallback method)
   */
  getLocalStorageData() {
    try {
      const rawData = localStorage.getItem(CollectionVehicleData.STORAGE_KEYS.STORAGE_KEY);
      if (!rawData) {
        return this.createEmptyVehicleData();
      }

      const cgfinderData = JSON.parse(rawData);
      
      const vehicleData = {
        metaobjectId: cgfinderData[CollectionVehicleData.STORAGE_KEYS.METAOBJECT_ID] || null,
        searchField: cgfinderData[CollectionVehicleData.STORAGE_KEYS.SEARCH_FIELD] || null,
        year: cgfinderData[CollectionVehicleData.STORAGE_KEYS.YEAR] || null,
        model: cgfinderData[CollectionVehicleData.STORAGE_KEYS.MODEL] || null,
        details: cgfinderData[CollectionVehicleData.STORAGE_KEYS.DETAILS] || null,
        category: cgfinderData[CollectionVehicleData.STORAGE_KEYS.CATEGORY] || null
      };

      return this.normalizeVehicleData(vehicleData);
      
    } catch (error) {
      console.warn('Error reading localStorage data:', error);
      return this.createEmptyVehicleData();
    }
  }

  /**
   * Create empty vehicle data structure
   */
  createEmptyVehicleData() {
    return {
      metaobjectId: null,
      searchField: null,
      year: null,
      model: null,
      details: null,
      category: null
    };
  }

  /**
   * Normalize vehicle data to ensure consistency
   */
  normalizeVehicleData(data) {
    return {
      metaobjectId: data.metaobjectId || null,
      searchField: data.searchField || null,
      year: data.year || null,
      model: data.model || null,
      details: data.details || null,
      category: data.category || null
    };
  }

  /**
   * Check if vehicle data is meaningful (has required fields)
   */
  hasValidVehicleData(vehicleData = null) {
    const data = vehicleData || this.getVehicleData();
    
    // Must have metaobjectId and at least year or model
    return !!(data.metaobjectId && (data.year || data.model));
  }

  /**
   * Get display text for vehicle selection
   */
  getVehicleDisplayText(vehicleData = null) {
    const data = vehicleData || this.getVehicleData();
    
    if (!this.hasValidVehicleData(data)) {
      return null;
    }

    // Format vehicle text
    if (data.year && data.model) {
      const details = data.details && data.details !== 'none' ? data.details : '';
      return `${data.year} ${data.model} ${details}`.trim();
    } 
    else if (data.year || data.model) {
      return data.year || data.model;
    }
    
    return 'Your Vehicle';
  }

  /**
   * Clear vehicle data from storage
   */
  clearVehicleData() {
    // Method 1: Use CGFinder's clear method if available
    if (this.isCGFinderAvailable() && typeof window.CGFinder.clearSelections === 'function') {
      console.log('🗑️ Clearing vehicle data via CGFinder');
      window.CGFinder.clearSelections();
    }
    // Method 2: Use EasyStatusCompatibility if available
    else if (typeof window.EasyStatusCompatibility !== 'undefined' && 
             typeof window.EasyStatusCompatibility.clearSelection === 'function') {
      console.log('🗑️ Clearing vehicle data via EasyStatusCompatibility');
      window.EasyStatusCompatibility.clearSelection();
    }
    // Method 3: Manual fallback
    else {
      console.log('🗑️ Clearing vehicle data manually');
      this.clearManually();
    }

    // Clear cache
    this.cache.clear();
  }

  /**
   * Manual clear fallback method
   */
  clearManually() {
    try {
      const rawData = localStorage.getItem(CollectionVehicleData.STORAGE_KEYS.STORAGE_KEY);
      if (!rawData) return;

      const cgfinderData = JSON.parse(rawData);
      
      // Remove vehicle-related keys
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.METAOBJECT_ID];
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.YEAR];
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.MODEL];
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.DETAILS];
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.CATEGORY];
      delete cgfinderData[CollectionVehicleData.STORAGE_KEYS.SEARCH_FIELD];
      
      localStorage.setItem(CollectionVehicleData.STORAGE_KEYS.STORAGE_KEY, JSON.stringify(cgfinderData));
      
    } catch (error) {
      console.warn('Error manually clearing CGFinder data:', error);
    }
  }

  /**
   * Listen for vehicle data changes
   */
  onDataChange(callback) {
    if (typeof callback !== 'function') return;

    // Listen for CGFinder events
    document.addEventListener('cgfinder:vehicleSelected', () => {
      this.cache.clear();
      setTimeout(() => callback(this.getVehicleData()), 100);
    });
    
    document.addEventListener('cgfinder:vehicleTemporarySelected', () => {
      this.cache.clear();
      setTimeout(() => callback(this.getVehicleData()), 100);
    });

    // Listen for form field changes to update display in real-time
    document.addEventListener('change', (e) => {
      // Check if the changed element is a CGFinder form field
      if (e.target && (
        e.target.name === 'cgfinder-year-select' || 
        e.target.name === 'cgfinder-model-select' || 
        e.target.name === 'cgfinder-details-select'
      )) {
        this.cache.clear();
        setTimeout(() => callback(this.getVehicleData()), 50);
      }
    });

    // Listen for storage events (for cross-tab updates)
    window.addEventListener('storage', (e) => {
      if (e.key === CollectionVehicleData.STORAGE_KEYS.STORAGE_KEY) {
        this.cache.clear();
        setTimeout(() => callback(this.getVehicleData()), 100);
      }
    });
  }

  /**
   * Get debug information
   */
  getDebugInfo() {
    const vehicleData = this.getVehicleData();
    
    return {
      vehicleData,
      hasValidData: this.hasValidVehicleData(vehicleData),
      displayText: this.getVehicleDisplayText(vehicleData),
      dataSource: this.isCGFinderAvailable() ? 'CGFinder' : 'localStorage',
      cacheSize: this.cache.size,
      storageKeys: CollectionVehicleData.STORAGE_KEYS
    };
  }

  /**
   * Invalidate cache (useful for testing or forced refresh)
   */
  invalidateCache() {
    this.cache.clear();
    console.log('🗑️ Collection vehicle data cache cleared');
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CollectionVehicleData = CollectionVehicleData;
}